import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface MetricsChartsProps {
  userStats?: {
    usersByType: {
      lawyer: number;
      firm: number;
      client: number;
    };
    usersByCountry: Record<string, number>;
  };
  documentStats?: {
    byCountry: Record<string, number>;
    byType: Record<string, number>;
    byStatus: Record<string, number>;
  };
  timeSeriesData?: Array<{
    date: string;
    users: number;
    documents: number;
  }>;
}

const COLORS = {
  tiber: "#1cb0a9",
  juridico: "#0A1C2E",
  green: "#10B981",
  yellow: "#F59E0B",
  red: "#EF4444",
};

const CHART_COLORS = [
  COLORS.tiber,
  COLORS.tiber,
  COLORS.green,
  COLORS.yellow,
  COLORS.red,
];

export const MetricsCharts: React.FC<MetricsChartsProps> = ({
  userStats,
  documentStats,
  timeSeriesData,
}) => {
  const { t } = useTranslation();

  // Prepare data for users by type chart
  const usersByTypeData = userStats
    ? [
        {
          name: t("admin.dashboard.lawyers"),
          value: userStats.usersByType.lawyer,
        },
        {
          name: t("admin.dashboard.firms"),
          value: userStats.usersByType.firm,
        },
        {
          name: t("admin.dashboard.clients"),
          value: userStats.usersByType.client,
        },
      ]
    : [];

  // Prepare data for users by country chart
  const usersByCountryData = userStats
    ? Object.entries(userStats.usersByCountry)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([country, count]) => ({
          name: country,
          value: count,
        }))
    : [];

  // Prepare data for documents by country chart
  const documentsByCountryData = documentStats
    ? Object.entries(documentStats.byCountry)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([country, count]) => ({
          name: country,
          value: count,
        }))
    : [];

  // Prepare data for documents by type chart
  const documentsByTypeData = documentStats
    ? Object.entries(documentStats.byType)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 8)
        .map(([type, count]) => ({
          name: type.length > 20 ? type.substring(0, 20) + "..." : type,
          fullName: type,
          value: count,
        }))
    : [];

  return (
    <div className="space-y-6">
      {/* Time Series Chart */}
      {timeSeriesData && timeSeriesData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg border border-romance-200 p-6"
        >
          <h3 className="text-lg font-bold text-juridico mb-4">
            {t("admin.metrics.charts.timeSeries")}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="date"
                stroke="#6B7280"
                style={{ fontSize: "12px" }}
              />
              <YAxis stroke="#6B7280" style={{ fontSize: "12px" }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#ffffff",
                  border: "2px solid var(--color-tiber-200)",
                  borderRadius: "12px",
                  padding: "10px 14px",
                  color: "var(--color-juridico)",
                  fontSize: "14px",
                }}
                labelStyle={{ color: "var(--color-juridico)", fontWeight: 600 }}
                itemStyle={{ color: "var(--color-tiber-700)" }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="users"
                stroke={COLORS.tiber}
                strokeWidth={2}
                name={t("admin.metrics.charts.users")}
                dot={{ fill: COLORS.tiber, r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="documents"
                stroke={COLORS.green}
                strokeWidth={2}
                name={t("admin.metrics.charts.documents")}
                dot={{ fill: COLORS.green, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users by Type Pie Chart */}
        {usersByTypeData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg border border-romance-200 p-6"
          >
            <h3 className="text-lg font-bold text-juridico mb-4">
              {t("admin.metrics.charts.usersByType")}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={usersByTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {usersByTypeData.map((_entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#ffffff",
                    border: "2px solid var(--color-tiber-200)",
                    borderRadius: "12px",
                    padding: "10px 14px",
                    color: "var(--color-juridico)",
                    fontSize: "14px",
                  }}
                  labelStyle={{ color: "var(--color-juridico)", fontWeight: 600 }}
                  itemStyle={{ color: "var(--color-tiber-700)" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>
        )}

        {/* Users by Country Bar Chart */}
        {usersByCountryData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg border border-romance-200 p-6"
          >
            <h3 className="text-lg font-bold text-juridico mb-4">
              {t("admin.metrics.charts.usersByCountry")}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={usersByCountryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="name"
                  stroke="#6B7280"
                  style={{ fontSize: "12px" }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis stroke="#6B7280" style={{ fontSize: "12px" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#ffffff",
                    border: "2px solid var(--color-tiber-200)",
                    borderRadius: "12px",
                    padding: "10px 14px",
                    color: "var(--color-juridico)",
                    fontSize: "14px",
                  }}
                  labelStyle={{ color: "var(--color-juridico)", fontWeight: 600 }}
                  itemStyle={{ color: "var(--color-tiber-700)" }}
                />
                <Bar
                  dataKey="value"
                  fill={COLORS.tiber}
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        )}

        {/* Documents by Country Bar Chart */}
        {documentsByCountryData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg border border-romance-200 p-6"
          >
            <h3 className="text-lg font-bold text-juridico mb-4">
              {t("admin.metrics.charts.documentsByCountry")}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={documentsByCountryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="name"
                  stroke="#6B7280"
                  style={{ fontSize: "12px" }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis stroke="#6B7280" style={{ fontSize: "12px" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#ffffff",
                    border: "2px solid var(--color-tiber-200)",
                    borderRadius: "12px",
                    padding: "10px 14px",
                    color: "var(--color-juridico)",
                    fontSize: "14px",
                  }}
                  labelStyle={{ color: "var(--color-juridico)", fontWeight: 600 }}
                  itemStyle={{ color: "var(--color-tiber-700)" }}
                />
                <Bar
                  dataKey="value"
                  fill={COLORS.green}
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        )}

        {/* Documents by Type Bar Chart */}
        {documentsByTypeData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-lg border border-romance-200 p-6"
          >
            <h3 className="text-lg font-bold text-juridico mb-4">
              {t("admin.metrics.charts.documentsByType")}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={documentsByTypeData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  type="number"
                  stroke="#6B7280"
                  style={{ fontSize: "12px" }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="#6B7280"
                  style={{ fontSize: "12px" }}
                  width={120}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#ffffff",
                    border: "2px solid var(--color-tiber-200)",
                    borderRadius: "12px",
                    padding: "10px 14px",
                    color: "var(--color-juridico)",
                    fontSize: "14px",
                  }}
                  labelStyle={{ color: "var(--color-juridico)", fontWeight: 600 }}
                  itemStyle={{ color: "var(--color-tiber-700)" }}
                  formatter={(value, name, props) => [
                    value ?? 0,
                    (props?.payload as { fullName?: string })?.fullName ?? name ?? "",
                  ]}
                />
                <Bar
                  dataKey="value"
                  fill={COLORS.tiber}
                  radius={[0, 8, 8, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        )}
      </div>
    </div>
  );
};
